import React, { lazy, Suspense, useState, useTransition, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { OnboardingProvider } from '../onboarding/context/OnboardingContext';
import OnboardingErrorBoundary from '../onboarding/components/OnboardingErrorBoundary';
import OnboardingShell from '../onboarding/components/shared/OnboardingShell';
import { getItem } from '../utility/localStorageControl';
import ProofStatsStep from '../onboarding/steps/ProofStatsStep';

// Lazy-loaded step components

const LandingScreen = lazy(() => import('../onboarding/steps/LandingScreen'));
const QualifyStep = lazy(() => import('../onboarding/steps/QualifyStep'));
const DemoTourStep = lazy(() => import('../onboarding/steps/DemoTourStep'));
const LetsGoStep = lazy(() => import('../onboarding/steps/LetsGoStep'));
const PlanStep = lazy(() => import('../onboarding/steps/PlanStep'));
const CustomizeStep = lazy(() => import('../onboarding/steps/CustomizeStep'));
const AgreementSign = lazy(() => import('../onboarding/steps/AgreementSign'));
const IdVerify = lazy(() => import('../onboarding/steps/IdVerify'));
const DamageHandle = lazy(() => import('../onboarding/steps/DamageHandle'));
const CoverageTiersStep = lazy(() => import('../onboarding/steps/CoverageTiersStep'));
const SocialProofStep = lazy(() => import('../onboarding/steps/SocialProofStep'));
const BookingApplyStep = lazy(() => import('../onboarding/steps/BookingApplyStep'));
const GuidebooksRevealStep = lazy(() => import('../onboarding/steps/GuidebooksRevealStep'));
const ArrivalChoiceStep          = lazy(() => import('../onboarding/steps/ArrivalChoiceStep'));
const ArrivalGuidebookPortalStep    = lazy(() => import('../onboarding/steps/ArrivalGuidebookPortalStep'));
const GuestRegistrationPortalStep       = lazy(() => import('../onboarding/steps/GuestRegistrationPortalStep'));
const GuestRegistrationOverviewStep     = lazy(() => import('../onboarding/steps/GuestRegistrationOverviewStep'));
const GuestRegistrationConfirmationStep = lazy(() => import('../onboarding/steps/GuestRegistrationConfirmationStep'));
const TaskIntroStep = lazy(() => import('../onboarding/steps/TaskIntroStep'));
const TaskDetailStep = lazy(() => import('../onboarding/steps/TaskDetailStep'));
const MessagingIntroStep = lazy(() => import('../onboarding/steps/MessagingIntroStep'));
const MessageTemplatesStep = lazy(() => import('../onboarding/steps/MessageTemplatesStep'));
const SmsRevealStep = lazy(() => import('../onboarding/steps/SmsRevealStep'));
const ConnectPmsStep = lazy(() => import('../onboarding/steps/ConnectPmsStep'));
const GuestRegistrationIntroStep = lazy(() => import('../onboarding/steps/GuestRegistrationIntroStep'));
const DisablePmsMessages = lazy(() => import('../onboarding/steps/DisablePmsMessages'));
const ReviewGuidebookStep = lazy(() => import('../onboarding/steps/ReviewGuidebookStep'));
const CompleteStep = lazy(() => import('../onboarding/steps/CompleteStep'));

const OnboardingProtectedRoute = ({ children }) => {
  const cookieToken = getItem('onboarding_access_token');
  if (!cookieToken) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

// Suspense fallback — minimal cream-background loader

const OnboardingLoader = () => (
  <div className="hg-nav-cold-loader">
    <div className="hg-nav-spinner" />
  </div>
);

const OnboardingRoutes = () => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      setDisplayLocation(location);
    });
  }, [location.pathname, location.search]);

  return (
    <OnboardingProvider>
      {/*
        No key wrapper here — keying the full tree caused the header to animate
        from opacity:0 on every navigation (visible flicker). The enter animation
        is now applied inside OnboardingLayout on .hg-left-panel only, so the
        header renders instantly while the content card slides in.
      */}
      <OnboardingErrorBoundary>
      <Suspense fallback={<OnboardingLoader />}>
        <Routes location={displayLocation}>
          <Route element={<OnboardingShell />}>
          <Route index element={<LandingScreen />} />

          <Route path="welcome" element={<Navigate to="/onboarding" replace />} />

          <Route
            path="guest-registration"
            element={
              <OnboardingProtectedRoute>
                <GuestRegistrationIntroStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="plan"
            element={
              <OnboardingProtectedRoute>
                <PlanStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="qualify"
            element={
              <OnboardingProtectedRoute>
                <QualifyStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="demo-tour"
            element={
              <OnboardingProtectedRoute>
                <DemoTourStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="lets-go"
            element={
              <OnboardingProtectedRoute>
                <LetsGoStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="customize"
            element={
              <OnboardingProtectedRoute>
                <CustomizeStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="sign"
            element={
              <OnboardingProtectedRoute>
                <AgreementSign />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="id-verify"
            element={
              <OnboardingProtectedRoute>
                <IdVerify />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="damage"
            element={
              <OnboardingProtectedRoute>
                <DamageHandle />
              </OnboardingProtectedRoute>
            }
          />
         
          <Route
            path="social-proof"
            element={
              <OnboardingProtectedRoute>
                <SocialProofStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="proof-stats"
            element={
              <OnboardingProtectedRoute>
                <ProofStatsStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="booking-apply"
            element={
              <OnboardingProtectedRoute>
                <BookingApplyStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="guidebooks-reveal"
            element={
              <OnboardingProtectedRoute>
                <GuidebooksRevealStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="arrival-choice"
            element={
              <OnboardingProtectedRoute>
                <ArrivalChoiceStep />
              </OnboardingProtectedRoute>
            }
          />
          <Route
            path="arrival-choice/:listingSlug"
            element={
              <OnboardingProtectedRoute>
                <ArrivalChoiceStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="arrival-confirmation"
            element={
              <OnboardingProtectedRoute>
                <ArrivalGuidebookPortalStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="registration-portal"
            element={
              <OnboardingProtectedRoute>
                <GuestRegistrationPortalStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="guest-registration-overview"
            element={
              <OnboardingProtectedRoute>
                <GuestRegistrationOverviewStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="guest-registration-confirmation"
            element={
              <OnboardingProtectedRoute>
                <GuestRegistrationConfirmationStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="task-intro"
            element={
              <OnboardingProtectedRoute>
                <TaskIntroStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="task-detail"
            element={
              <OnboardingProtectedRoute>
                <TaskDetailStep />
              </OnboardingProtectedRoute>
            }
          />

          <Route
            path="coverage"
            element={
              <OnboardingProtectedRoute>
                <CoverageTiersStep />
              </OnboardingProtectedRoute>
            }
          />
          <Route
            path="connect-pms"
            element={
              <OnboardingProtectedRoute>
                <ConnectPmsStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="disable-pms-messages"
            element={
              <OnboardingProtectedRoute>
                <DisablePmsMessages />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="guidebook"
            element={
              <OnboardingProtectedRoute>
                <ReviewGuidebookStep />
              </OnboardingProtectedRoute>
            }
          />
          <Route
            path="guidebook/:listingSlug"
            element={
              <OnboardingProtectedRoute>
                <ReviewGuidebookStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="messaging-intro"
            element={
              <OnboardingProtectedRoute>
                <MessagingIntroStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="messaging-templates"
            element={
              <OnboardingProtectedRoute>
                <MessageTemplatesStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="sms-reveal"
            element={
              <OnboardingProtectedRoute>
                <SmsRevealStep />
              </OnboardingProtectedRoute>
            }
          />
          
          <Route
            path="complete"
            element={
              <OnboardingProtectedRoute>
                <CompleteStep />
              </OnboardingProtectedRoute>
            }
          />

          {/* Catch-all: redirect unknown onboarding sub-paths to landing */}
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </Route>
        </Routes>
      </Suspense>
      </OnboardingErrorBoundary>
    </OnboardingProvider>
  );
};

export default OnboardingRoutes;
